import {
    CoreGridInput,
    CoreProduct,
    CoreProductPayload,
    CoreProductsGridOutput,
    CoreAvailabilityInput,
    expeditionScanModeFromType,
    fromCoreProductNames,
    GridInput,
    GridOutput,
    legacyScanImeiSnToType,
    Product,
    ProductAvailabilityCount,
    ProductAvailabilityInput,
    ProductFileType,
    ScanImeiSnType,
    scanSerialNumberBooleanToType,
    toCoreProductNames
} from 'skladon-types';
import type {
    CoreProductAvailability,
    CoreProductAvailabilityCount,
} from 'skladon-types';

import { CoreGridMapper } from './CoreGridMapper';

const INSERT_TO_ORDER_RELATION_TYPES = new Set(['insertToOrder', 'insertToOrderMultiple']);

function mapInsertToProductSkus(coreProduct: Partial<CoreProduct>): string[] {
    const relations = coreProduct.PRODUCT_RELATIONS_2 ?? [];
    const skus = relations
        .filter((relation) => relation.TYPE != null && INSERT_TO_ORDER_RELATION_TYPES.has(relation.TYPE))
        .map((relation) => relation.PRODUCT_1?.SKU?.trim() ?? '')
        .filter((sku): sku is string => sku.length > 0);
    return [...new Set(skus)];
}

function mapInsertToCountries(coreProduct: Partial<CoreProduct>): string[] {
    const countries = (coreProduct.PRODUCT_INSERT_COUNTRIES ?? [])
        .map((row) => row.COUNTRY?.trim() ?? '')
        .filter((country): country is string => country.length > 0);
    return [...new Set(countries)];
}

export class ProductsMapper {
    /**
     * Priority: scanImeiSnType → deprecated scanImeiSn (0/1/2) → deprecated scanSerialNumber (boolean).
     */
    private static mapScanImeiSnType(product: Product): ScanImeiSnType | undefined {
        if (product.scanImeiSnType !== undefined) {
            return product.scanImeiSnType;
        }

        if (product.scanImeiSn !== undefined) {
            return legacyScanImeiSnToType(product.scanImeiSn);
        }

        if (product.scanSerialNumber !== undefined) {
            return scanSerialNumberBooleanToType(product.scanSerialNumber);
        }

        return undefined;
    }

    public static fromCore(coreProduct: Partial<CoreProduct>): Partial<Product> {
        const mapped: Partial<Product> = {
            id: coreProduct.ID,
            expMethod: coreProduct.EXP_METHOD,
            hsCode: coreProduct.HS_CODE,
            isbn: coreProduct.ISBN,
            mpn: coreProduct.MPN,
            name: coreProduct.NAME,
            note: coreProduct.NOTE,
            price: coreProduct.PRICE,
            productType: coreProduct.PRODUCT_TYPE,
            rop: coreProduct.ROP,
            sku: coreProduct.SKU,
            guid: coreProduct.GUID,
            upc: coreProduct.UPC,
            ageCheck: !!coreProduct.AGECHECK,
            customField1: coreProduct.CUSTOM_FIELD_1,
            customField2: coreProduct.CUSTOM_FIELD_2,
            customField3: coreProduct.CUSTOM_FIELD_3,
            description: coreProduct.DESCRIPTION,
            ean: coreProduct.EAN,
            eans: coreProduct.EANS,
            eanLockedByDcix: coreProduct.EAN_LOCKED_BY_DCIX,
            active: !!coreProduct.ACTIVE,
            scanImeiSnType: coreProduct.SCAN_IMEI_SN_TYPE,
            scanSerialNumber:
                coreProduct.SCAN_IMEI_SN_TYPE !== undefined
                    ? expeditionScanModeFromType(coreProduct.SCAN_IMEI_SN_TYPE) !== 0
                    : !!coreProduct.SCAN_SERIAL_NUMBER,
            scanImeiSn:
                coreProduct.SCAN_IMEI_SN_TYPE !== undefined
                    ? expeditionScanModeFromType(coreProduct.SCAN_IMEI_SN_TYPE)
                    : coreProduct.SCAN_SERIAL_NUMBER !== undefined
                      ? (Number(coreProduct.SCAN_SERIAL_NUMBER) as 0 | 1 | 2)
                      : undefined,
            originCountry: coreProduct.ORIGIN_COUNTRY,
            isDeleted: !!coreProduct.IS_DELETED,
            batteryType: coreProduct.BATTERY_TYPE,
            snRegex: coreProduct.SN_REGEX,
            unitPrice: coreProduct.UNIT_PRICE,
            weight: coreProduct.WEIGHT,
            width: coreProduct.WIDTH,
            height: coreProduct.HEIGHT,
            length: coreProduct.LENGTH,
            insertToProductSkus: mapInsertToProductSkus(coreProduct),
            insertToCountries: mapInsertToCountries(coreProduct),
            insertType: coreProduct.INSERT_TYPE,
            insertByOrderType: coreProduct.INSERT_BY_ORDER_TYPE,
            insertPriceFrom: coreProduct.INSERT_PRICE_FROM,
            insertPriceTo: coreProduct.INSERT_PRICE_TO,
            expirationDays: coreProduct.EXPIRATION_DAYS,
            createdAt: coreProduct.CREATED_AT,
            updatedAt: coreProduct.UPDATED_AT,
            dimensionsChangedAt: coreProduct.DIMENSIONS_CHANGED_AT,
            deletedAt: coreProduct.DELETED_AT ?? null,
            quantityConversion: coreProduct.QUANTITY_CONVERSION,
            sourceSys: coreProduct.SOURCE_SYS ?? null,
            files: coreProduct.FILES?.length
                ? coreProduct.FILES.map((f) => ({
                      id: f.FILE_ID,
                      type: f.TYPE as ProductFileType,
                      fileName: f.ORIGINAL_FILE_NAME,
                      fileType: f.FILE_TYPE,
                      fileSize: f.FILE_SIZE,
                      createdAt: f.CREATED_AT ?? null,
                      creatorRole: f.CREATOR_ROLE ?? null
                  }))
                : []
        };

        return mapped;
    }

    public static toCore(product: Product, ownerEshopKey: string): CoreProductPayload {
        const scanImeiSnType = ProductsMapper.mapScanImeiSnType(product);
        return {
            OWNER_ESHOP_KEY: ownerEshopKey,
            ACTIVE: product.active !== undefined ? Number(product.active) : undefined,
            AGECHECK: product.ageCheck !== undefined ? Number(product.ageCheck) : undefined,
            CUSTOM_FIELD_1: product.customField1,
            CUSTOM_FIELD_2: product.customField2,
            CUSTOM_FIELD_3: product.customField3,
            DESCRIPTION: product.description,
            EAN: product.ean,
            EANS: product.eans,
            EXP_METHOD: product.expMethod,
            GUID: product.guid,
            HS_CODE: product.hsCode,
            ISBN: product.isbn,
            MPN: product.mpn,
            NAME: product.name,
            NOTE: product.note,
            PRICE: product.price,
            PRODUCT_TYPE: product.productType,
            ROP: product.rop,
            SKU: product.sku,
            UPC: product.upc,
            IS_DELETED: product.isDeleted !== undefined ? Number(product.isDeleted) : undefined,
            SCAN_IMEI_SN_TYPE: scanImeiSnType,
            SCAN_SERIAL_NUMBER: scanImeiSnType !== undefined ? expeditionScanModeFromType(scanImeiSnType) : undefined,
            ORIGIN_COUNTRY: product.originCountry,
            BATTERY_TYPE: product.batteryType,
            SN_REGEX: product.snRegex,
            UNIT_PRICE: product.unitPrice,
            EXPIRATION_DAYS: product.expirationDays,
            FILES: Array.isArray(product.files)
                ? product.files.length
                    ? product.files.map((f) => ({
                          id: f.id,
                          data: f.data,
                          fileName: f.fileName,
                          type: f.type,
                          fileType: f.fileType,
                          fileSize: f.fileSize
                      }))
                    : []
                : undefined
        };
    }

    public static fromCoreAvailability(
        productAvailabilities: CoreProductAvailabilityCount[]
    ): ProductAvailabilityCount[] {
        return productAvailabilities.map(
            (productAvailability: CoreProductAvailabilityCount): ProductAvailabilityCount => {
                const existsInCore =
                    (productAvailability as CoreProductAvailability | undefined)?.error !==
                    'Cannot find product with given SKU';

                const mapItem = (
                    item: NonNullable<CoreProductAvailabilityCount['items']>[number]
                ): NonNullable<ProductAvailabilityCount['items']>[number] => ({
                    awaiting: item.AWAITING ?? null,
                    all: item.ALL ?? null,
                    onExpedition: item.ON_EXPEDITION ?? null,
                    onHoldQuantity: item.ON_HOLD_QUANTITY ?? null,
                    dutyQuantity: item.DUTY_QUANTITY ?? null,
                    allocated: item.ALLOCATED ?? null,
                    available: item.AVAILABLE ?? null,
                    expirationDate: item.EXPIRATION_DATE ?? null,
                    lot: item.LOT ?? null
                });

                return {
                    all: existsInCore ? productAvailability.ALL || 0 : null,
                    sku: productAvailability.SKU!,
                    awaiting: existsInCore ? productAvailability.AWAITING || 0 : null,
                    onExpedition: existsInCore ? productAvailability.ON_EXPEDITION || 0 : null,
                    onHoldQuantity: existsInCore ? productAvailability.ON_HOLD_QUANTITY || 0 : null,
                    dutyQuantity: existsInCore ? productAvailability.DUTY_QUANTITY || 0 : null,
                    allocated: existsInCore ? productAvailability.ALLOCATED || 0 : null,
                    available: existsInCore ? productAvailability.AVAILABLE || 0 : null,
                    error: !existsInCore ? 'Cannot find product with given SKU' : null,
                    items: productAvailability.items?.map(mapItem)
                };
            }
        );
    }

    public static toCoreAvailability(productsSkus: ProductAvailabilityInput): CoreAvailabilityInput {
        return {
            products: productsSkus.skus.map((sku: string) => ({
                SKU: sku
            }))
        };
    }

    public static fromCoreGrid(grid: CoreProductsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreProduct) => ProductsMapper.fromCore(coreProduct))
                .map((product) => CoreGridMapper.omitColumns(product, fromCoreProductNames, grid.request.columns)),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreProductNames, 1000, ['active', 'ageCheck']),
            totalCount: grid.totalCount
        };
    }

    public static toCoreGrid(grid: GridInput): CoreGridInput {
        return CoreGridMapper.mapGrid(grid, toCoreProductNames, 1000);
    }
}
